import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaTriangleExclamation, FaClock } from 'react-icons/fa6';
import { localDB } from '../../../../../../../../crud/localDB/localDB';
import './SessionAlert.scss';

interface SessionAlertProps {
  sessionStart: Date;
  onEndSession: () => void;
}

const SessionAlert: React.FC<SessionAlertProps> = ({ sessionStart, onEndSession }) => {
  const [show, setShow] = useState(false);
  const [userChoseToEnd, setUserChoseToEnd] = useState(false); // Flag to prevent re-showing

  const TWENTY_HOURS_MS = 20 * 60 * 60 * 1000; // 20 hours in milliseconds
  const FIVE_MINUTES_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Calculate session duration
  const getSessionDuration = () => {
    const now = new Date().getTime();
    const start = sessionStart.getTime();
    return now - start;
  };

  // Format duration for display
  const formatDuration = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Check if we should show the alert
  const shouldShowAlert = async () => {
    // Don't show if user already chose to end the session
    if (userChoseToEnd) {
      return false;
    }

    const duration = getSessionDuration();
    
    // Only show if session is longer than 20 hours
    if (duration < TWENTY_HOURS_MS) {
      return false;
    }

    // Check when user last acknowledged the alert
    const lastAcknowledged = await localDB.get('alerts', 'sessionTime');
    
    if (!lastAcknowledged) {
      return true; // First time showing alert
    }

    const timeSinceLastAck = Date.now() - (lastAcknowledged as number);
    
    // Show again if 5 minutes have passed since last acknowledgment
    return timeSinceLastAck >= FIVE_MINUTES_MS;
  };

  // Handle user acknowledgment
  const handleAcknowledge = async () => {
    await localDB.set('alerts', 'sessionTime', Date.now());
    setShow(false);
  };

  // Handle end session
  const handleEndSession = () => {

    setUserChoseToEnd(true); // Prevent modal from showing again
    setShow(false); // Close the alert modal first
    setTimeout(() => {
      onEndSession(); // Then trigger the end session modal with slight delay
    }, 100); // Small delay to ensure state update processes
  };

  // Check periodically if we should show the alert
  useEffect(() => {
    const checkAlert = async () => {
      const should = await shouldShowAlert();
      if (should && !show) {
        setShow(true);
      }
    };

    // Check immediately
    checkAlert();

    // Check every minute
    const interval = setInterval(checkAlert, 60 * 1000);

    return () => clearInterval(interval);
  }, [sessionStart, show, userChoseToEnd]);

  return (
    <Modal 
      show={show} 
      onHide={() => {}} // Prevent closing by clicking outside
      backdrop="static" // Prevent closing by clicking backdrop
      keyboard={false} // Prevent closing with ESC key
      className="session-alert-modal"
      centered
    >
      <Modal.Header className="session-alert-header">
        <div className="alert-icon">
          <FaTriangleExclamation />
        </div>
        <Modal.Title className="session-alert-title">
          Sesión Prolongada Detectada
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="session-alert-body">
        <div className="duration-display">
          <FaClock className="clock-icon" />
          <span className="duration-text">
            {formatDuration(getSessionDuration())}
          </span>
        </div>
        
        <div className="alert-message">
          <p className="primary-message">
            Tu sesión ha estado activa por más de 20 horas.
          </p>
          <p className="secondary-message">
            Es posible que hayas olvidado cerrarla. ¿Deseas finalizar la sesión ahora?
          </p>
        </div>

        <div className="alert-note">
          <small>
            Si decides continuar, este recordatorio aparecerá nuevamente en 5 minutos.
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer className="session-alert-footer">
        <Button 
          variant="none" 
          className="session-alert-btn continue-btn"
          onClick={handleAcknowledge}
        >
          Continuar Sesión
        </Button>
        <Button 
          variant="none" 
          className="session-alert-btn end-btn"
          onClick={handleEndSession}
        >
          <FaClock className="btn-icon" />
          Finalizar Sesión
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SessionAlert;